import Phaser from 'phaser';
import { TILE_SIZE, TileType, isBlocking, isWallMass, type MapChunkPacket } from '@game/protocol';

import {
  Cut,
  EDGES_TEXTURE,
  Material,
  PROPS_TEXTURE,
  Prop,
  SHADE_N,
  SHADE_NW,
  SHADE_TEXTURE,
  SHADE_W,
  SURFACE_COUNT,
  Surface,
  TILESET_TEXTURE,
  crownOf,
  cutFrame,
  edgeFrame,
  groundFrame,
  materialOf,
  materialOfSurface,
  propFrame,
  propOf,
  surfaceOf,
  tileVariant,
} from '../art/tiles.ts';
import { scrolledOrigin, windowSize } from './terrain-window.ts';

/**
 * O terreno da região do lado do cliente: o que já chegou, em qual andar, e como
 * desenhá-lo.
 *
 * O cenário chega por `MapChunk` conforme o jogador se aproxima (PRD §6), e esta classe é
 * quem costura os pedaços: guarda o que recebeu, redesenha só o que mudou e responde à
 * predição de movimento sobre o que sabe.
 *
 * ## Cinco camadas
 *
 * O byte que vem do servidor diz apenas "aqui é grama" ou "aqui é árvore". Tudo o que
 * separa isso de um tabuleiro é derivado da **vizinhança**, no cliente, e distribuído em
 * cinco camadas de tilemap desenhadas nesta ordem:
 *
 * | camada     | o que desenha                                        | de onde vem       |
 * |------------|------------------------------------------------------|-------------------|
 * | `ground`   | o material do próprio tile                           | o tile            |
 * | `edges`    | a borda do material vizinho transbordando neste tile | os 8 vizinhos     |
 * | `shade`    | a sombra de parede e árvore                          | N, O e NO         |
 * | `props`    | a metade de baixo de árvore, pedra e moita           | o tile            |
 * | `propsTop` | a metade de cima da peça do tile **de baixo**        | o vizinho ao sul  |
 *
 * São cinco porque um tile pode precisar de tudo isso ao mesmo tempo — a beira de uma
 * estrada rente a um muro, sob a copa de uma árvore, é um caso comum — e uma camada de
 * tilemap só desenha um quadro por célula.
 *
 * ## A camada tem o tamanho da janela, não o da região
 *
 * A primeira versão criava as camadas com o **tamanho total** da região, e a troca era
 * consciente: cada camada aloca um objeto de tile por célula, mas em troca o Phaser faz
 * culling de câmera nela e um chunk que chega é um `putTileAt` em vez de reconstruir a
 * camada. A troca estava certa para 80 × 64. Deixou de estar quando a região passou a poder
 * ser maior que a tela (PRD §15.1): numa região de 512 × 512 × 8 o Welcome alocaria 2,6
 * milhões de objetos de tile, e cada `setFloor` redesenharia 262.144 células.
 *
 * A camada agora tem o tamanho de uma **janela** que rola com a câmera — a viewport
 * arredondada para chunk com um chunk de folga de cada lado, limitada pela região quando ela
 * é menor. A aritmética de deslocamento mora inteira em `terrain-window.ts` e num único par
 * de conversões aqui (`localX`/`localY`); nenhum `putTileAt` recebe coordenada de mundo.
 *
 * O que se perde é o culling de câmera, que deixa de vir de graça — mas a janela já é
 * aproximadamente a câmera. E o `patch` barato deixa de valer para todo chunk: um chunk que
 * chega fora da janela grava só no `Uint8Array`, e a faixa que entra é desenhada quando a
 * janela rola.
 *
 * Cada camada tem um `Tilemap` próprio, e não um tileset a mais no mesmo mapa: o Phaser
 * numera os quadros de tilesets irmãos em faixas sequenciais, e um mapa com cinco
 * tilesets exigiria somar deslocamentos a cada `putTileAt` — aritmética que erra em
 * silêncio no dia em que uma textura mudar de tamanho.
 */

/** Ordem de desenho. Bem abaixo de zero: entidades usam profundidade da linha da grade. */
const DEPTH = {
  ground: -1000,
  edges: -996,
  shade: -992,
  props: -988,
  propsTop: -984,
} as const;

interface Layer {
  map: Phaser.Tilemaps.Tilemap;
  layer: Phaser.Tilemaps.TilemapLayer | null;
}

/**
 * Contagem de superfícies do anel de `surfaceAt`, reaproveitada entre chamadas (PRD §15.1).
 *
 * `surfaceAt` é chamada nove vezes por célula desenhada — uma para a célula e uma por
 * vizinho, dentro de `drawEdges` — e alocava um `Array(6)` por chamada. Numa troca de andar
 * são 55.296 alocações, e é a mesma disciplina que §3.1 impõe ao servidor: zero alocação no
 * caminho quente.
 *
 * O tamanho vem de `SURFACE_COUNT`, e não de um literal: o buffer anterior tinha seis
 * posições para sete materiais, e escrever fora do fim de um `Uint8Array` **não** dá erro —
 * some. Na prática a lava nunca ganhava a votação do chão de uma peça plantada nela, e a
 * única forma de perceber era desenhar uma árvore à beira do lago de lava.
 *
 * Um buffer de módulo é seguro aqui porque `surfaceAt` não é reentrante: ela chama
 * `surfaceOf`, que é pura, e nunca a si mesma. O `fill(0)` na entrada é o que garante que
 * a chamada anterior não vaza para esta.
 */
const SURFACE_COUNTS = new Uint8Array(SURFACE_COUNT);

/** Vizinhos na ordem dos bits da máscara de borda: N, NE, L, SE, S, SO, O, NO. */
const NEIGHBOURS = [
  [0, -1],
  [1, -1],
  [1, 0],
  [1, 1],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [-1, -1],
] as const;

/**
 * As quatro quinas, na ordem dos bits de `Cut`: NE, SE, SO, NO.
 *
 * Cada uma é um trio de índices em `NEIGHBOURS`: a **diagonal** e os **dois lados** que a
 * formam. A quina só é convexa — e só então vale cortar — quando os três apontam para fora do
 * material deste tile: com um dos lados igual ao próprio material, a massa continua por ali e
 * o que parecia quina é meio de uma margem reta.
 */
const CORNERS = [
  { bit: Cut.NorthEast, diagonal: 1, a: 0, b: 2 },
  { bit: Cut.SouthEast, diagonal: 3, a: 2, b: 4 },
  { bit: Cut.SouthWest, diagonal: 5, a: 4, b: 6 },
  { bit: Cut.NorthWest, diagonal: 7, a: 6, b: 0 },
] as const;

/**
 * Buffers do caminho quente de `drawEdges`, reaproveitados entre chamadas.
 *
 * Mesmo argumento de `MATERIAL_COUNTS`: a função roda uma vez por célula desenhada e uma
 * troca de andar redesenha a janela inteira. Um vetor por célula seria a alocação que §3.1
 * proíbe no caminho quente. `-1` é "não é material" — parede, ou fora do mapa.
 */
const NEIGHBOUR_MATERIALS = new Int8Array(8);
const CORNER_MATERIALS = new Int8Array(4);
const CUT_COUNTS = new Uint8Array(7);

/**
 * O material que morde esta quina, ou `-1` quando ela não é convexa.
 *
 * Os três vizinhos que a formam têm de ser todos mais fracos que o deste tile. Basta um deles
 * ser do mesmo material para a massa continuar por ali, e cortar ali abriria um buraco no meio
 * de uma margem reta.
 *
 * A peça desenhada é a do material da **diagonal**, porque é ela que ocupa o espaço que o
 * corte revela. Parede responde `-1` por `materialAt`, e é o que impede que o calçamento de
 * dentro de uma casa tenha as quinas comidas pelos próprios cantos da construção.
 */
function cornerMaterial(corner: (typeof CORNERS)[number], material: Material): number {
  const diagonal = NEIGHBOUR_MATERIALS[corner.diagonal] ?? -1;
  if (diagonal < 0 || diagonal >= material) return -1;

  const a = NEIGHBOUR_MATERIALS[corner.a] ?? -1;
  if (a < 0 || a >= material) return -1;

  const b = NEIGHBOUR_MATERIALS[corner.b] ?? -1;
  if (b < 0 || b >= material) return -1;

  return diagonal;
}

export class Terrain {
  readonly width: number;
  readonly height: number;
  readonly floors: number;

  /** `width * height * floors`, preenchido com `Unknown` até o chunk chegar. */
  private readonly tiles: Uint8Array;

  private readonly ground: Layer;
  private readonly edges: Layer;
  private readonly shade: Layer;
  private readonly props: Layer;
  private readonly propsTop: Layer;

  /** Lado da janela desenhada, em tiles. Ver `terrain-window.ts`. */
  private readonly windowWidth: number;
  private readonly windowHeight: number;

  /** Canto superior esquerdo da janela, em tiles de mundo. */
  private originX = 0;
  private originY = 0;

  private currentFloor = 0;

  constructor(
    scene: Phaser.Scene,
    width: number,
    height: number,
    floors: number,
    viewTilesX = width,
    viewTilesY = height,
  ) {
    this.width = width;
    this.height = height;
    this.floors = floors;
    this.tiles = new Uint8Array(width * height * floors).fill(TileType.Unknown);

    this.windowWidth = windowSize(viewTilesX, width);
    this.windowHeight = windowSize(viewTilesY, height);

    const w = this.windowWidth;
    const h = this.windowHeight;

    this.ground = makeLayer(scene, w, h, TILESET_TEXTURE, DEPTH.ground);
    this.edges = makeLayer(scene, w, h, EDGES_TEXTURE, DEPTH.edges);
    this.shade = makeLayer(scene, w, h, SHADE_TEXTURE, DEPTH.shade);
    this.props = makeLayer(scene, w, h, PROPS_TEXTURE, DEPTH.props);
    this.propsTop = makeLayer(scene, w, h, PROPS_TEXTURE, DEPTH.propsTop);

    this.placeLayers();
  }

  get floor(): number {
    return this.currentFloor;
  }

  /** A janela em tiles de mundo — o retângulo que está efetivamente desenhado. */
  get window(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.originX,
      y: this.originY,
      width: this.windowWidth,
      height: this.windowHeight,
    };
  }

  private index(x: number, y: number, z: number): number {
    return (z * this.height + y) * this.width + x;
  }

  /**
   * Rola a janela para acompanhar o personagem, e redesenha se ela se moveu.
   *
   * Chamado a cada mudança de posição de grade. Na esmagadora maioria das vezes não faz
   * nada: a histerese de `scrolledOrigin` só recoloca a janela quando o jogador entra na
   * faixa de um chunk junto à borda, o que acontece a cada 16 tiles caminhados. Quando
   * acontece, o custo é um redesenho da janela — as mesmas 6.144 células que o `setFloor`
   * já custava, e não as 262.144 da região inteira.
   */
  follow(x: number, y: number): void {
    const nextX = scrolledOrigin(x, this.originX, this.windowWidth, this.width);
    const nextY = scrolledOrigin(y, this.originY, this.windowHeight, this.height);

    if (nextX === this.originX && nextY === this.originY) return;

    this.originX = nextX;
    this.originY = nextY;
    this.placeLayers();
    this.redrawWindow();
  }

  private placeLayers(): void {
    const px = this.originX * TILE_SIZE;
    const py = this.originY * TILE_SIZE;

    for (const layer of this.layers()) layer.layer?.setPosition(px, py);
  }

  private layers(): readonly Layer[] {
    return [this.ground, this.edges, this.shade, this.props, this.propsTop];
  }

  /** Coluna dentro da janela, ou `-1` se o tile está fora dela. */
  private localX(x: number): number {
    const local = x - this.originX;
    return local >= 0 && local < this.windowWidth ? local : -1;
  }

  /** Linha dentro da janela, ou `-1` se o tile está fora dela. */
  private localY(y: number): number {
    const local = y - this.originY;
    return local >= 0 && local < this.windowHeight ? local : -1;
  }

  /**
   * Tile já recebido, ou `Unknown` no que ainda não chegou.
   *
   * Público porque o minimapa desenha a partir daqui (ver `ui/minimap.ts`): ele mostra
   * exatamente o que o cliente conhece, e é o mesmo vetor que a predição de movimento
   * consulta — duas leituras da mesma verdade, em vez de uma cópia para desenhar.
   */
  at(x: number, y: number, z: number): number {
    // Fora do mapa responde parede: é o que faz a predição recusar um passo para fora da
    // região sem precisar de uma checagem de limite separada em cada chamador.
    if (x < 0 || y < 0 || z < 0 || x >= this.width || y >= this.height || z >= this.floors) {
      return TileType.Wall;
    }
    return this.tiles[this.index(x, y, z)] ?? TileType.Unknown;
  }

  /** Grava um pedaço recebido e redesenha o que ele afeta, se for do andar visível. */
  patch(chunk: MapChunkPacket): void {
    for (let row = 0; row < chunk.height; row++) {
      const source = row * chunk.width;
      this.tiles.set(
        chunk.tiles.subarray(source, source + chunk.width),
        this.index(chunk.x, chunk.y + row, chunk.z),
      );
    }

    if (chunk.z !== this.currentFloor) return;

    // Uma moldura de um tile em volta do pedaço: a aparência de um tile depende dos
    // vizinhos — a borda de material olha os oito, a sombra olha três e a copa olha o
    // tile de baixo — então a primeira fileira fora do chunk acabou de mudar de aparência
    // sem ter mudado de tipo. Sem isso, a costura entre chunks vira uma grade de emendas
    // a cada 16 tiles.
    this.redraw(chunk.x - 1, chunk.y - 1, chunk.width + 2, chunk.height + 2);
  }

  /**
   * Um tile mudou (PRD §15.2). Grava e redesenha a moldura em volta dele.
   *
   * A moldura de um tile, e não só a célula: a aparência de um tile depende dos vizinhos —
   * a borda de material olha os oito, a sombra olha três e a copa olha o de baixo —, então
   * abrir uma porta muda a sombra que ela projetava no tile de baixo. É a mesma razão pela
   * qual `patch` redesenha uma borda em volta do chunk.
   *
   * Devolve `false` quando o tile está fora do mapa: quem chama usa isso para não forçar um
   * redesenho de minimapa por um pacote que não mudou nada.
   */
  patchTile(x: number, y: number, z: number, tile: number): boolean {
    if (x < 0 || y < 0 || z < 0 || x >= this.width || y >= this.height || z >= this.floors) {
      return false;
    }

    this.tiles[this.index(x, y, z)] = tile;

    if (z === this.currentFloor) this.redraw(x - 1, y - 1, 3, 3);
    return true;
  }

  /** Troca o andar visível e redesenha **a janela** a partir do que já chegou. */
  setFloor(z: number): void {
    if (z === this.currentFloor) return;
    this.currentFloor = z;
    this.redrawWindow();
  }

  private redrawWindow(): void {
    this.redraw(this.originX, this.originY, this.windowWidth, this.windowHeight);
  }

  /**
   * Redesenha um retângulo de mundo, cortado pela janela.
   *
   * O corte é aqui e não em cada chamador porque é a única fronteira: quem pede redesenho
   * pensa em coordenada de mundo — o chunk que chegou, o tile que mudou, a janela que rolou
   * — e a conversão acontece uma vez.
   */
  private redraw(x0: number, y0: number, width: number, height: number): void {
    const minX = Math.max(this.originX, x0);
    const minY = Math.max(this.originY, y0);
    const maxX = Math.min(this.originX + this.windowWidth, x0 + width);
    const maxY = Math.min(this.originY + this.windowHeight, y0 + height);

    for (let y = minY; y < maxY; y++) {
      for (let x = minX; x < maxX; x++) {
        this.drawCell(x, y);
      }
    }
  }

  private drawCell(x: number, y: number): void {
    const column = this.localX(x);
    const row = this.localY(y);
    if (column < 0 || row < 0) return;

    const z = this.currentFloor;
    const tile = this.at(x, y, z);
    const variant = tileVariant(x, y);

    // Terreno não recebido fica vazio em vez de virar um tile de "nada": a célula vazia
    // não desenha, o que é honesto — ali ainda não se sabe o que há.
    if (tile === TileType.Unknown) {
      this.ground.layer?.removeTileAt(column, row);
      this.edges.layer?.removeTileAt(column, row);
      this.shade.layer?.removeTileAt(column, row);
      this.props.layer?.removeTileAt(column, row);
      this.drawCanopy(x, y);
      return;
    }

    const surface = this.surfaceAt(x, y, z);

    this.ground.layer?.putTileAt(
      groundFrame(
        tile,
        variant,
        surface ?? Surface.Grass,
        this.isMasonry(x, y, z, tile),
        // Coroamento só no tile mais alto da massa de parede, sombra de contato só no
        // mais baixo. Ver `faceOffset` em `art/tiles.ts`: é o que impede que uma muralha
        // de três tiles apareça como três blocos listrados empilhados.
        //
        // As duas **quinas** de cima entram junto, e são elas que consertam a encosta em
        // diagonal: onde a rocha continua subindo para um dos lados, a faixa clara morre
        // naquele lado em vez de atravessar o tile inteiro (ver `Crown`). Sem isso, cada
        // degrau de uma diagonal vem com uma régua luminosa em cima e a encosta lê como um
        // lance de escada de blocos — a queixa de "está tudo muito quadrado" na tela.
        //
        // A pergunta é sobre a **massa** e não sobre o tipo: um veio de minério no meio de
        // uma parede é parede, e tratá-lo como vazio abriria uma faixa de coroamento
        // iluminado no meio da rocha, como se ali houvesse céu.
        crownOf(
          isWallMass(this.at(x, y - 1, z)),
          isWallMass(this.at(x - 1, y - 1, z)),
          isWallMass(this.at(x + 1, y - 1, z)),
        ),
        !isWallMass(this.at(x, y + 1, z)),
      ),
      column,
      row,
    );
    this.drawEdges(x, y, z, surface === null ? null : materialOfSurface(surface));
    this.drawShade(x, y, z, tile);

    const prop = propOf(tile);
    if (prop === null) this.props.layer?.removeTileAt(column, row);
    else this.props.layer?.putTileAt(propFrame(prop, variant, 0), column, row);

    this.drawCanopy(x, y);
  }

  /**
   * A metade de cima da peça que está no tile **de baixo**.
   *
   * É o que dá altura à árvore: a copa ocupa a célula de trás, que continua sendo chão
   * caminhável. A alternativa — um sprite por árvore — custaria um objeto de cena por
   * tronco do mapa e jogaria fora o culling de tilemap.
   */
  private drawCanopy(x: number, y: number): void {
    const column = this.localX(x);
    const row = this.localY(y);
    if (column < 0 || row < 0) return;

    const below = propOf(this.at(x, y + 1, this.currentFloor));

    if (below === null) this.propsTop.layer?.removeTileAt(column, row);
    else this.propsTop.layer?.putTileAt(propFrame(below, tileVariant(x, y + 1), 1), column, row);
  }

  /**
   * Esta parede é alvenaria ou rocha bruta?
   *
   * O servidor manda `Wall` para as duas: para a colisão são idênticas, e um segundo tipo
   * de tile só para o desenho seria informação de arte trafegando no protocolo. Quem
   * decide é o que há **em volta** — parede encostada em calçamento é construção, e o
   * resto é encosta de montanha ou parede de caverna.
   *
   * A regra erra num caso: um muro de pedra solto no campo, longe de qualquer piso, sai
   * como rocha. É o erro certo a cometer — muro solto no campo não existe neste mundo, e
   * encosta de montanha existe às centenas de tiles.
   */
  private isMasonry(x: number, y: number, z: number, tile: number): boolean {
    // O veio de minério é sempre rocha bruta: ele só existe na galeria, e alvenaria com
    // minério embutido seria uma parede que ninguém construiu assim.
    if (tile === TileType.OreVein) return false;
    if (tile !== TileType.Wall) return true;

    for (const [dx, dy] of NEIGHBOURS) {
      if (materialOf(this.at(x + dx, y + dy, z)) === Material.Pavement) return true;
    }
    return false;
  }

  /**
   * Superfície do solo deste tile — o piso que será **desenhado** nele.
   *
   * Árvore, pedra, moita, poste, placa e engradado não trazem o próprio chão no byte do tile
   * — são objetos **sobre** o solo. O piso vem então da vizinhança: o mais frequente entre os
   * oito vizinhos, e num raio maior se o primeiro anel só tiver mais peça. Sem isso, um
   * bosque no meio de uma clareira de terra apareceria como um tabuleiro de grama.
   *
   * Superfície, e não material (ver `Surface` em `art/tiles.ts`): a resposta é usada para
   * escolher o quadro do chão, e três pisos da cidade compartilham o material `Pavement`.
   * Respondendo em material, um poste plantado na rua de paralelepípedo recebia a laje de
   * dentro das casas, e a cidade inteira ficava salpicada de tapetes claros quadrados sob a
   * mobília. Para a mistura de bordas, `materialOfSurface` desfaz a distinção de volta.
   */
  private surfaceAt(x: number, y: number, z: number): Surface | null {
    const tile = this.at(x, y, z);
    if (isWallMass(tile)) return null;

    const own = surfaceOf(tile);
    if (own !== null) return own;

    for (const radius of [1, 2]) {
      const counts = SURFACE_COUNTS.fill(0);
      let found = false;

      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (dx === 0 && dy === 0) continue;
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== radius) continue;

          const neighbour = surfaceOf(this.at(x + dx, y + dy, z));
          // Água não empresta chão a uma árvore: um tronco sobre a lâmina d'água leria
          // como bug, não como mangue.
          if (neighbour === null || neighbour === Surface.Water) continue;

          counts[neighbour] = (counts[neighbour] ?? 0) + 1;
          found = true;
        }
      }

      if (!found) continue;

      let best = Surface.Grass as Surface;
      for (let candidate = 0; candidate < counts.length; candidate++) {
        if ((counts[candidate] ?? 0) > (counts[best] ?? 0)) best = candidate as Surface;
      }
      return best;
    }

    return Surface.Grass;
  }

  /**
   * A borda do material vizinho transbordando sobre este tile.
   *
   * Só o vizinho de **maior** prioridade entra, e só ele: a camada desenha um quadro por
   * célula, e empilhar terra e areia no mesmo tile exigiria uma sexta camada para ganhar
   * uma diferença que ninguém enxerga. O de maior prioridade é o certo porque a ordem foi
   * escolhida justamente pela dominância visual — água molha areia, areia cobre terra.
   */
  private drawEdges(x: number, y: number, z: number, material: Material | null): void {
    const layer = this.edges.layer;
    if (!layer) return;

    const column = this.localX(x);
    const row = this.localY(y);
    if (column < 0 || row < 0) return;

    if (material === null) {
      layer.removeTileAt(column, row);
      return;
    }

    // Os oito vizinhos, uma vez só. `surfaceAt` não é barata — para árvore, pedra e moita
    // ela varre dois anéis atrás do chão que a peça está cobrindo — e a versão anterior a
    // chamava dezesseis vezes por célula, uma vez para achar o vizinho mais forte e outra
    // para montar a máscara dele. Numa troca de andar são 6.144 células.
    //
    // A mistura é por **material**: aqui a laje, o paralelepípedo e o piso consagrado voltam
    // a ser a mesma coisa, que é o que os faz não desenharem borda entre si na esquina de uma
    // loja. A distinção de superfície só interessa a quem escolhe o quadro do chão.
    let strongest = -1;

    for (const [index, [dx, dy]] of NEIGHBOURS.entries()) {
      const surface = this.surfaceAt(x + dx, y + dy, z);
      const neighbour = surface === null ? null : materialOfSurface(surface);
      NEIGHBOUR_MATERIALS[index] = neighbour ?? -1;
      if (neighbour !== null && neighbour > material && neighbour > strongest) {
        strongest = neighbour;
      }
    }

    // Ninguém mais forte em volta: este tile é o material dominante do trecho, e o que resta
    // é arredondar as quinas convexas dele. Ver `drawCornerCut`.
    if (strongest < 0) {
      this.drawCornerCut(layer, material, column, row);
      return;
    }

    let mask = 0;
    for (let index = 0; index < NEIGHBOUR_MATERIALS.length; index++) {
      if (NEIGHBOUR_MATERIALS[index] === strongest) mask |= 1 << index;
    }

    layer.putTileAt(edgeFrame(strongest as Material, mask), column, row);
  }

  /**
   * Morde as quinas convexas deste tile com o material mais fraco que as cerca (PRD §15.8).
   *
   * É a metade que faltava da transição, e o argumento inteiro está em `cutFrame`: a camada
   * de bordas só sabia derramar o material forte sobre o fraco, então **o forte continuava
   * sendo um quadrado**. Uma poça de um tile no charco era um quadrado azul de 32 px com
   * espuma em volta; uma de cinco tiles em cruz era uma cruz de quadrados. É a queixa de que
   * "está tudo muito quadrado" na sua forma mais literal — a silhueta da água era a grade.
   *
   * ## Cabe na mesma camada, e não numa sexta
   *
   * Um tile que tem vizinho mais forte recebe o transbordo **dele** e nunca chega aqui; um
   * que não tem deixaria a célula vazia. Os dois casos são disjuntos por construção, então o
   * corte ocupa a célula que sobrava em vez de custar uma camada de tilemap a mais — e uma
   * camada a mais custaria um objeto de tile por célula da janela, que é o que §15.1 acabou
   * de gastar trabalho para reduzir.
   *
   * ## Um material por tile, o que reivindicar mais quinas
   *
   * Uma célula desenha um quadro só, e um quadro é de um material só. Onde duas quinas de um
   * mesmo tile encostam em materiais diferentes — a ponta de uma estrada entre a areia e a
   * grama —, corta-se pelo que aparece mais e a outra quina fica reta. É o erro barato: a
   * quina não cortada é o desenho que já existia.
   */
  private drawCornerCut(
    layer: Phaser.Tilemaps.TilemapLayer,
    material: Material,
    column: number,
    row: number,
  ): void {
    CUT_COUNTS.fill(0);
    let best = -1;

    for (const [index, corner] of CORNERS.entries()) {
      const at = cornerMaterial(corner, material);
      CORNER_MATERIALS[index] = at;
      if (at < 0) continue;

      const count = (CUT_COUNTS[at] ?? 0) + 1;
      CUT_COUNTS[at] = count;
      if (best < 0 || count > (CUT_COUNTS[best] ?? 0)) best = at;
    }

    if (best < 0) {
      layer.removeTileAt(column, row);
      return;
    }

    let cuts = 0;
    for (const [index, corner] of CORNERS.entries()) {
      if (CORNER_MATERIALS[index] === best) cuts |= corner.bit;
    }

    layer.putTileAt(cutFrame(best as Material, cuts), column, row);
  }

  /**
   * Sombra projetada por parede, só do norte, do oeste e da quina noroeste — a luz do
   * cenário inteiro vem de cima e da esquerda.
   *
   * **Só parede.** A primeira versão incluía árvore e pedra, e o efeito foi o contrário do
   * pretendido: a sombra de tilemap é uma faixa reta colada na divisa da célula, e num
   * bosque cada árvore escurecia os dois vizinhos da mesma forma. As faixas se somavam,
   * o campo inteiro virava um tabuleiro escuro e as únicas células claras eram as das
   * próprias árvores — exatamente o quadriculado que este módulo existe para eliminar.
   *
   * O que assenta árvore e pedra no chão é a elipse desenhada no pé da peça, dentro da
   * própria arte (ver `art/tiles.ts`): ela acompanha a silhueta em vez de acompanhar a
   * grade, que é a diferença entre sombra e retângulo escuro. Parede continua aqui porque
   * ela **é** a célula inteira: nela a faixa reta é o desenho certo.
   */
  private drawShade(x: number, y: number, z: number, tile: number): void {
    const layer = this.shade.layer;
    if (!layer) return;

    const column = this.localX(x);
    const row = this.localY(y);
    if (column < 0 || row < 0) return;

    // Parede não recebe sombra: ela já tem o próprio topo iluminado e o pé escuro, e uma
    // sombra por cima disso viraria borrão numa muralha longa.
    if (isWallMass(tile)) {
      layer.removeTileAt(column, row);
      return;
    }

    const casts = (tx: number, ty: number): boolean => isWallMass(this.at(tx, ty, z));

    const mask =
      (casts(x, y - 1) ? SHADE_N : 0) |
      (casts(x - 1, y) ? SHADE_W : 0) |
      (casts(x - 1, y - 1) ? SHADE_NW : 0);

    if (mask === 0) layer.removeTileAt(column, row);
    else layer.putTileAt(mask, column, row);
  }

  /**
   * O tile do andar visível é atravessável?
   *
   * Não é uma segunda autoridade de colisão — é o que evita que a predição de movimento
   * (PRD §3.2) dê um passo que o servidor vai recusar meio RTT depois. Terreno ainda não
   * recebido bloqueia, por `isBlocking(Unknown)`.
   */
  isWalkable(x: number, y: number): boolean {
    return !isBlocking(this.at(x, y, this.currentFloor));
  }

  /** A cena continua viva entre regiões; o que morre aqui é só o mundo anterior. */
  destroy(): void {
    for (const layer of this.layers()) {
      layer.layer?.destroy();
      layer.map.destroy();
    }
  }
}

function makeLayer(
  scene: Phaser.Scene,
  width: number,
  height: number,
  texture: string,
  depth: number,
): Layer {
  const map = scene.make.tilemap({
    width,
    height,
    tileWidth: TILE_SIZE,
    tileHeight: TILE_SIZE,
  });

  // Margem e espaçamento zero: o tileset é gerado por nós, tile a tile encostado, e não
  // tem a moldura que ferramentas de empacotamento costumam pôr.
  const tileset = map.addTilesetImage(texture, texture, TILE_SIZE, TILE_SIZE, 0, 0);
  const layer = tileset ? map.createBlankLayer(texture, tileset) : null;
  layer?.setDepth(depth);

  return { map, layer };
}

/** Reexportado para quem monta cenário fora daqui — o efeito de portal, por exemplo. */
export { Prop };
